import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "../../constants/Colors";
import StepWrapper from "./StepWrapper";
import {
  CreateFlatStore,
  AddressSuggestion,
} from "../../hooks/useCreateFlatStore";
import { debounce } from "../../utils/debounce";

const countryToCurrency: Record<string, string> = {
  ch: "CHF",
  de: "EUR",
  at: "EUR",
  fr: "EUR",
  it: "EUR",
  es: "EUR",
  nl: "EUR",
  be: "EUR",
  gb: "GBP",
  us: "USD",
};

interface Props {
  store: CreateFlatStore;
  onLocationSelected: () => void;
}

const LocationStep: React.FC<Props> = ({ store, onLocationSelected }) => {
  const { t } = useTranslation();
  const { textNeutralDefault, textNeutralMuted, accentColor } = useColors();
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const europeBoundingBox = "-12.69,36.29,24.57,58.84";
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query,
        )}&viewbox=${europeBoundingBox}&bounded=1&addressdetails=1`,
        { headers: { "User-Agent": "flateroo/1.0 webapp" } },
      );
      const data: AddressSuggestion[] = await response.json();

      const unique = data.filter(
        (s, i, arr) =>
          i === arr.findIndex((x) => x.display_name === s.display_name),
      );
      setSuggestions(unique);
    } catch (e) {
      console.error("Error fetching location suggestions:", e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedFetch = useMemo(
    () => debounce((query: string) => fetchSuggestions(query), 300),
    [fetchSuggestions],
  );

  const handleInputChange = (value: string) => {
    store.setStreet(value);
    debouncedFetch(value);
  };

  const handleSelectSuggestion = (suggestion: AddressSuggestion) => {
    const countryCode = suggestion.address?.country_code?.toLowerCase() || "";
    const currency = countryToCurrency[countryCode] || "EUR";

    const locationData = {
      ...suggestion,
      currency,
    };

    store.setLatitude(suggestion.lat);
    store.setLongitude(suggestion.lon);
    store.setLocation(JSON.stringify(locationData));
    store.setStreet(suggestion.display_name);
    setSuggestions([]);
    onLocationSelected();
  };

  const clearLocation = () => {
    store.setStreet("");
    store.setLatitude("");
    store.setLongitude("");
    store.setLocation("");
    setSuggestions([]);
  };

  const hasSelectedLocation = !!store.latitude && !!store.longitude;

  return (
    <StepWrapper
      title={
        t("flats_tab.add_flat.flat_location_address") ||
        "Where is your flat located?"
      }
      subtitle={
        t("flats_tab.add_flat.flat_location_address_subtitle") ||
        "Enter the address"
      }
    >
      <div className="space-y-4">
        <div className="relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: textNeutralMuted }}
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          <input
            type="text"
            value={store.street}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={
              t("flats_tab.add_flat.flat_location_placeholder") ||
              "Search for an address..."
            }
            className="cf-input-boxed"
            style={{
              color: textNeutralDefault,
              paddingLeft: 44,
              paddingRight: store.street ? 44 : 18,
            }}
          />
          {store.street && (
            <button
              onClick={clearLocation}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
              style={{ backgroundColor: "rgba(2, 2, 16, 0.06)" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: textNeutralMuted }}
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 px-2 py-1">
            <div
              className="w-2.5 h-2.5 rounded-full animate-pulse flex-shrink-0"
              style={{ backgroundColor: accentColor, opacity: 0.5 }}
            />
            <span className="text-sm leading-none" style={{ color: textNeutralMuted }}>
              {t("loading") || "Searching..."}
            </span>
          </div>
        )}

        {hasSelectedLocation && (
          <div
            className="px-4 py-3.5 rounded-2xl flex items-center gap-3"
            style={{ backgroundColor: "rgba(91, 61, 246, 0.06)" }}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: accentColor }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <span className="text-sm font-medium" style={{ color: textNeutralDefault }}>
              {t("location_selected") || "Location selected"}
            </span>
          </div>
        )}

        {suggestions.length > 0 && !hasSelectedLocation && (
          <div
            className="rounded-2xl overflow-hidden"
            style={{ backgroundColor: "rgba(2, 2, 16, 0.03)" }}
          >
            {suggestions.slice(0, 5).map((suggestion, index) => (
              <button
                key={suggestion.place_id || index}
                onClick={() => handleSelectSuggestion(suggestion)}
                className="w-full px-4 py-3.5 text-left transition-colors flex items-center gap-3 hover:bg-black/[0.03]"
                style={{
                  borderBottom:
                    index < Math.min(suggestions.length, 5) - 1
                      ? "1px solid rgba(2, 2, 16, 0.06)"
                      : "none",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ color: textNeutralMuted, flexShrink: 0 }}
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span
                  className="text-sm line-clamp-2 leading-snug"
                  style={{ color: textNeutralDefault }}
                >
                  {suggestion.display_name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </StepWrapper>
  );
};

export default LocationStep;
